/**
 * Regional locales: own UI messages and market, while editorial content falls back to the base
 * locale until a regional text exists. `pt-br` is not regional; it is a standalone locale without
 * a base fallback. Plain dependency-free ESM because next.config.js and tsx scripts import it;
 * src/config/locale.ts wraps the table in typed helpers, and every surface reads those helpers
 * instead of comparing locale strings. Never named in customer-facing copy.
 */

/**
 * @typedef {object} RegionalLocaleSpec
 * @property {"es"} base Locale whose editorial content the regional locale falls back to.
 * @property {"eu" | "us"} primaryChannel Search target and destination of a bare `/<locale>` path.
 * @property {readonly ("eu" | "us")[]} indexableChannels Channels on which the locale is indexed.
 * @property {string} country Country that selects the locale on entry when the browser language
 *   is the base language or missing.
 * @property {string} referenceCurrency Reference display currency without a country signal.
 * @property {string} checkoutCountry Home country in checkout.
 * @property {string} timeZone IANA zone for mail send windows.
 */

/** @type {Readonly<{ "es-mx": Readonly<RegionalLocaleSpec> }>} */
export const REGIONAL_LOCALES = Object.freeze({
	"es-mx": Object.freeze({
		base: "es",
		primaryChannel: "us",
		indexableChannels: Object.freeze(["us"]),
		country: "MX",
		referenceCurrency: "MXN",
		checkoutCountry: "MX",
		timeZone: "America/Mexico_City",
	}),
});

/**
 * next.config.js headers() entries: an HTTP-level noindex for each regional locale on every
 * channel outside its `indexableChannels` (es-mx: `/es-mx/eu`). Lives in config headers for the
 * same reason as usNoindexHeaderRule in us-indexing.mjs.
 *
 * @param {readonly string[]} [channels]
 * @returns {{ source: string, headers: { key: string, value: string }[] }[]}
 */
export function regionalNoindexHeaderRules(channels = ["eu", "us"]) {
	return Object.entries(REGIONAL_LOCALES).flatMap(([locale, spec]) =>
		channels
			.filter((channel) => !spec.indexableChannels.some((indexable) => indexable === channel))
			.map((channel) => ({
				source: `/:locale(${locale})/${channel}/:path*`,
				headers: [{ key: "X-Robots-Tag", value: "noindex, follow" }],
			})),
	);
}
